import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createBillingPeriod, CreateBillingPeriod } from "@/services/billingPeriodServices";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CreatePlanPeriod = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState<CreateBillingPeriod>({
        label: "",
        months: 1,
        discountPercent: 0,
        active: true,
    });

    const [errors, setErrors] = useState<Partial<Record<keyof CreateBillingPeriod, string>>>({});

    const createMutation = useMutation({
        mutationFn: createBillingPeriod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["billingPeriods"] });
            navigate("/subscriptions-manage");
        },
        onError: (error: Error) => {
            alert(t('errorCreatingBillingPeriod', { message: error.message }));
        },
    });

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof CreateBillingPeriod, string>> = {};

        if (!formData.label.trim()) {
            newErrors.label = t('nameIsRequired');
        }

        if (formData.months < 1) {
            newErrors.months = t('monthsMustBeAtLeast1');
        }

        if (formData.discountPercent < 0 || formData.discountPercent > 100) {
            newErrors.discountPercent = t('discountMustBeBetween0And100');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (validateForm()) {
            createMutation.mutate(formData);
        }
    };

    const handleChange = (field: keyof CreateBillingPeriod, value: string | number | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Limpiar error del campo al cambiar
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    return (
        <section className="space-y-6">
            <div className="flex items-center gap-4">
                <Link
                    to="/subscriptions-manage"
                    className="rounded-lg p-2 text-gray-600 transition hover:bg-gray-100"
                >
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('createBillingPeriod')}</h1>
                    <p className="text-sm text-gray-600">{t('defineNewBillingPeriodWithDiscount')}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="max-w-2xl space-y-6 mx-auto">
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('periodInformation')}</h2>

                    <div className="space-y-5">
                        {/* Label */}
                        <div>
                            <label htmlFor="label" className="mb-2 block text-sm font-medium text-gray-700">
                                {t('periodName')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="label"
                                type="text"
                                value={formData.label}
                                onChange={(e) => handleChange("label", e.target.value)}
                                placeholder={t('periodNamePlaceholder')}
                                className={`w-full rounded-lg border ${
                                    errors.label ? "border-red-500" : "border-gray-300"
                                } px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                            />
                            {errors.label && (
                                <p className="mt-1 text-xs text-red-500">{errors.label}</p>
                            )}
                        </div>

                        {/* Months */}
                        <div>
                            <label htmlFor="months" className="mb-2 block text-sm font-medium text-gray-700">
                                {t('numberOfMonths')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="months"
                                type="number"
                                min="1"
                                step="1"
                                value={formData.months}
                                onChange={(e) => handleChange("months", parseInt(e.target.value) || 0)}
                                onKeyDown={(e) => {
                                    if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                                        e.preventDefault();
                                    }
                                }}
                                className={`w-full rounded-lg border ${
                                    errors.months ? "border-red-500" : "border-gray-300"
                                } px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                            />
                            {errors.months && (
                                <p className="mt-1 text-xs text-red-500">{errors.months}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">{t('periodDurationInMonths')}</p>
                        </div>

                        {/* Discount Percent */}
                        <div>
                            <label htmlFor="discountPercent" className="mb-2 block text-sm font-medium text-gray-700">
                                {t('discountPercentage')} <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    id="discountPercent"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={formData.discountPercent}
                                    onChange={(e) => handleChange("discountPercent", parseFloat(e.target.value) || 0)}
                                    className={`w-full rounded-lg border ${
                                        errors.discountPercent ? "border-red-500" : "border-gray-300"
                                    } px-4 py-2 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                                    %
                                </span>
                            </div>
                            {errors.discountPercent && (
                                <p className="mt-1 text-xs text-red-500">{errors.discountPercent}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">{t('discountAppliedToMonthlyPrice')}</p>
                        </div>

                        {/* Active */}
                        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <input
                                id="active"
                                type="checkbox"
                                checked={formData.active}
                                onChange={(e) => handleChange("active", e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500"
                            />
                            <label htmlFor="active" className="text-sm font-medium text-gray-700">
                                {t('activePeriod')}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Vista previa */}
                <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-6 shadow-sm">
                    <h3 className="mb-4 text-sm font-semibold text-indigo-900">{t('preview')}</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-700">{t('name')}:</span>
                            <span className="font-semibold text-gray-900">
                                {formData.label || t('noName')}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-700">{t('duration')}:</span>
                            <span className="font-semibold text-gray-900">
                                {formData.months} {formData.months === 1 ? t('month') : t('months')}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-700">{t('discount')}:</span>
                            <span className="font-semibold text-green-600">
                                {formData.discountPercent}%
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-700">{t('status')}:</span>
                            <span className={`font-semibold ${formData.active ? "text-green-600" : "text-gray-500"}`}>
                                {formData.active ? t('active') : t('inactive')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex gap-4">
                    <button
                        type="button"
                        onClick={() => navigate("/plans")}
                        className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                        disabled={createMutation.isPending}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="submit"
                        disabled={createMutation.isPending}
                        className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {createMutation.isPending ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t('creating')}
                            </>
                        ) : (
                            t('createPeriod')
                        )}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default CreatePlanPeriod;